import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Wallet, Gem, Crown, Gift, ChevronRight, X, Sparkles, ImagePlus, Phone, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const DISMISS_KEY = "bloq_activation_dismissed";

// First-run activation nudge. Identity-first order; 2FA is OPTIONAL (doesn't block completion).
// Auto-hides once all REQUIRED steps are complete or the user dismisses it.
export function ActivationChecklist() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  const { data: walletData } = useQuery({ queryKey: ["wallet-mine"], queryFn: api.walletMine, retry: false, staleTime: 60000 });
  const { data: subsData } = useQuery({ queryKey: ["my-subscriptions"], queryFn: api.mySubscriptions, retry: false, staleTime: 60000 });
  const { data: refData } = useQuery({ queryKey: ["referrals-me"], queryFn: api.referralsMe, retry: false, staleTime: 60000 });

  const walletVerified = (walletData?.wallets || []).length > 0;
  const hasAvatar = !!user?.avatar_b64;
  const phoneVerified = !!user?.phone_verified;
  const twofaOn = !!user?.twofa_sms;
  const nftSet = !!user?.nft_avatar;
  const hasSub = (subsData?.subscriptions || []).length > 0;
  // Referral progress field names vary by backend rev — read the same canonical set ReferralsPage uses.
  const referralsJoined = refData?.referrals_joined ?? refData?.joined ?? refData?.referrals_qualified ?? refData?.qualified ?? refData?.referral_count ?? 0;
  const hasInvited = referralsJoined > 0;

  const steps = [
    { key: "avatar", done: hasAvatar, icon: ImagePlus, title: "Add a profile picture", desc: "Put a face to your handle", go: () => navigate("/app/profile?edit=1") },
    { key: "phone", done: phoneVerified, points: 13, icon: Phone, title: "Verify your phone number", desc: "Boost your trust score and unlock SMS 2FA", go: () => navigate("/app/settings?focus=phone") },
    { key: "wallet", done: walletVerified, points: 3, icon: Wallet, title: "Verify your wallet", desc: "Prove ownership to unlock your Web3 identity", go: () => navigate("/app/wallet") },
    { key: "nft", done: nftSet, icon: Gem, title: "Set an NFT profile picture", desc: walletVerified ? "Show your gem ring across the app" : "Verify a wallet first, then pick an NFT", go: () => navigate("/app/wallet") },
    { key: "twofa", done: twofaOn, optional: true, icon: ShieldCheck, title: "Set up 2FA", desc: phoneVerified ? "Add a login code by SMS" : "Verify your phone first, then enable", go: () => navigate("/app/settings?focus=2fa") },
    { key: "sub", done: hasSub, icon: Crown, title: "Support a creator", desc: "Subscribe to a fan tier to unlock member perks", go: () => navigate("/creators") },
    { key: "invite", done: hasInvited, icon: Gift, title: "Invite friends", desc: "You both get 7 days of BLOQ+ free", go: () => navigate("/app/referrals") },
  ];

  const required = steps.filter((s) => !s.optional);
  const doneCount = required.filter((s) => s.done).length;

  if (dismissed || doneCount === required.length) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="mb-3 rounded-2xl border border-bloq-purple/25 bg-bloq-purple/[0.06] p-3.5" data-testid="activation-checklist">
      <div className="mb-2.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-sm font-bold text-white">
          <Sparkles className="h-4 w-4 text-bloq-purple" /> Get started on BLOQ Talk
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-bloq-purple" data-testid="activation-progress">{doneCount}/{required.length}</span>
          <button onClick={dismiss} className="text-bloq-muted transition-colors hover:text-white" title="Dismiss" data-testid="activation-dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="space-y-1.5">
        {steps.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => !s.done && s.go()}
              disabled={s.done}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition-colors",
                s.done ? "cursor-default border-bloq-mint/20 bg-bloq-mint/[0.06]" : "border-white/8 bg-bloq-s2 hover:bg-white/5",
              )}
              data-testid={`activation-step-${s.key}`}
            >
              {s.done ? <CheckCircle2 className="h-5 w-5 shrink-0 text-bloq-mint" /> : <Icon className="h-5 w-5 shrink-0 text-bloq-purple" />}
              <div className="min-w-0 flex-1">
                <div className={cn("flex items-center gap-1.5 text-sm font-semibold", s.done ? "text-bloq-mint line-through" : "text-white")}>
                  {s.title}
                  {s.optional && !s.done && <span className="rounded-full bg-white/8 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-bloq-muted">Optional</span>}
                  {s.points && !s.done && (
                    <span className="flex items-center gap-0.5 rounded-full bg-bloq-mint/15 px-1.5 py-0.5 text-[9px] font-bold text-bloq-mint" data-testid={`activation-points-${s.key}`}>
                      +{s.points} trust
                    </span>
                  )}
                </div>
                {!s.done && <div className="truncate text-[11px] text-bloq-muted">{s.desc}</div>}
              </div>
              {!s.done && <ChevronRight className="h-4 w-4 shrink-0 text-bloq-muted" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
